﻿<template>
  <v-container fluid class="amplify-container pa-4">
    <div class="layout-wrapper">

      <!-- ── Phone Frame (left) ── -->
      <div class="phone-section">
        <div class="phone-outer-frame">

          <!-- Left-side hardware buttons -->
          <div class="phone-btn btn-silent"></div>
          <div class="phone-btn btn-vol-up"></div>
          <div class="phone-btn btn-vol-down"></div>

          <!-- Right-side hardware button -->
          <div class="phone-btn btn-power"></div>

          <!-- Screen -->
          <div class="phone-screen">
           

            <iframe
              ref="iframeRef"
              :src="APP_ORIGIN"
              allowfullscreen
              title="Gecko Amplify App"
              aria-label="Mobile app preview (Gecko Amplify)"
              class="amplify-iframe"
              @load="onIframeLoad"
            />

            <!-- Home indicator bar -->
            <div class="home-indicator"></div>
          </div>

        </div>
      </div>

      <!--  Tools Panel (right)  -->
      <div class="tools-section">
        <div class="tools-panel">
          <div class="tools-panel-title">Tools</div>
          <div class="tools-panel-body">

            <!-- Refresh App -->
            <Button
              label="Refresh App"
              icon="pi pi-refresh"
              severity="secondary"
              text
              fluid
              class="tools-btn"
              @click="refreshIframe"
            />

            <Divider />

            <!--  Move My Account  -->
            <div class="tools-section-label">Move My Account</div>

            <div class="tools-inner">
              <!-- Current account badge -->
              <div class="current-account-badge" v-if="!loadingSelf">
                <i class="pi pi-user" />
                <span v-if="myAccountId">Currently on account <strong>#{{ myAccountId }}</strong></span>
                <span v-else class="text-color-secondary">Account not loaded</span>
              </div>
              <div v-else class="current-account-badge">
                <ProgressSpinner style="width:14px;height:14px" stroke-width="6" />
                <span class="text-color-secondary">Loading profile</span>
              </div>

              <!-- Account search autocomplete -->
              <AutoComplete
                v-model="accountSearch"
                :suggestions="accountSuggestions"
                option-label="label"
                placeholder="Search by email or account ID"
                fluid
                :loading="loadingAccountSearch"
                force-selection
                @complete="onSearchAccounts"
                @option-select="onAccountSelected"
                @clear="onSearchCleared"
                class="mt-2"
              />

              <!-- Selected account preview -->
              <div v-if="selectedAccount" class="selected-account-preview">
                <div class="selected-account-header">
                  <span class="font-semibold">Account #{{ selectedAccount.accountId }}</span>
                  <span class="text-color-secondary text-sm">{{ selectedAccount.email }}</span>
                </div>

                <div v-if="loadingTargetUsers" class="flex align-items-center gap-2 mt-1">
                  <ProgressSpinner style="width:14px;height:14px" stroke-width="6" />
                  <span class="text-sm">Loading users</span>
                </div>
                <Message
                  v-else-if="loadedTargetUsers && !targetAccountExists"
                  severity="error"
                  :closable="false"
                  class="mt-1"
                >
                  Account not found
                </Message>
                <Message
                  v-else-if="loadedTargetUsers && selectedAccount.accountId === myAccountId"
                  severity="warn"
                  :closable="false"
                  class="mt-1"
                >
                  That is your current account
                </Message>
                <div
                  v-else-if="loadedTargetUsers && targetAccountUsers && targetAccountUsers.length > 0"
                  class="target-users-list"
                >
                  <div
                    v-for="u of targetAccountUsers.slice(0, TARGET_USERS_PREVIEW_LIMIT)"
                    :key="u.userId"
                    class="target-user-row"
                  >
                    <i class="pi pi-user text-xs" />
                    <span>{{ u.firstName }} {{ u.lastName }}<span v-if="u.email" class="text-color-secondary"> - {{ u.email }}</span></span>
                  </div>
                  <div v-if="targetAccountUsers.length > TARGET_USERS_PREVIEW_LIMIT" class="text-xs text-color-secondary">
                    +{{ targetAccountUsers.length - TARGET_USERS_PREVIEW_LIMIT }} more
                  </div>
                </div>
                <p v-else-if="loadedTargetUsers" class="text-xs text-color-secondary mt-1">No users in this account</p>
              </div>

              <!-- Action buttons -->
              <div class="action-buttons">
                <Button
                  label="Move Me to This Account"
                  icon="pi pi-arrows-h"
                  severity="warn"
                  fluid
                  :disabled="!canMove"
                  :loading="movingSelf"
                  @click="moveSelf"
                />
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>
  </v-container>

</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { useToast } from 'primevue/usetoast'
import AutoComplete from 'primevue/autocomplete'
import Button from 'primevue/button'
import Divider from 'primevue/divider'
import Message from 'primevue/message'
import ProgressSpinner from 'primevue/progressspinner'
import { useBreadCrumbsStore } from '../stores/breadCrumbs'
import { useApi } from '../api'
import { auth0, getAccessTokenSilentlyOutsideComponent } from '../lib/auth'
import { handleApiError, isApiNotFoundError } from '../lib/utils'
import type { AccountSearchResultV1, AccountUserV1 } from '@geckoal/gecko-api-client'

// ── Constants ──
const APP_ORIGIN = validateAppOrigin(import.meta.env.VITE_APP_URL)
const LOG_PREFIX = '[GeckoApp]'
const SEARCH_MIN_LENGTH = 2
const TOAST_DURATION = 4000
const TOAST_DURATION_LONG = 6000
const TARGET_USERS_PREVIEW_LIMIT = 3

type IframeMessage = {
  type: 'AUTH_TOKEN' | 'REQUEST_TOKEN'
  access_token?: string
  id_token?: string
}

/**
 * Validates that VITE_APP_URL is configured and returns it.
 * Throws if not set to prevent runtime errors.
 */
function validateAppOrigin(url: string | undefined): string {
  if (!url) {
    throw new Error('VITE_APP_URL environment variable is not configured')
  }
  return url
}

const log = (...args: unknown[]) => console.log(LOG_PREFIX, ...args)
const logWarn = (...args: unknown[]) => console.warn(LOG_PREFIX, ...args)
const logError = (...args: unknown[]) => console.error(LOG_PREFIX, ...args)

function isValidMessageOrigin(origin: string): boolean {
  return origin === APP_ORIGIN
}

const toast = useToast()

const breadCrumbsStore = useBreadCrumbsStore()
const { user: auth0UserProfile } = useAuth0()
const iframeRef = ref<HTMLIFrameElement | null>(null)

// ── Self / current admin ──
const mySelf = ref<AccountUserV1 | undefined>()
const myAccountId = ref<number | undefined>()
const loadingSelf = ref(false)

async function loadSelf(): Promise<void> {
  const userId = auth0UserProfile.value?.sub
  if (!userId) return
  if (mySelf.value) return // already loaded
  try {
    loadingSelf.value = true
    const result = await useApi().getUserV1({ userId })
    mySelf.value = result.user
    myAccountId.value = result.account.accountId
    log('loaded self user profile:', mySelf.value.userId)
  } catch (err) {
    logError('failed to load self user profile:', err)
  } finally {
    loadingSelf.value = false
  }
}

// Trigger loadSelf as soon as auth0 user becomes available
watchEffect(() => {
  if (auth0UserProfile.value?.sub) loadSelf()
})

// ── Account search & move ──
type AccountSuggestion = AccountSearchResultV1 & { label: string }

const accountSearch = ref<AccountSuggestion | string | null>(null)
const accountSuggestions = ref<AccountSuggestion[]>([])
const loadingAccountSearch = ref(false)
const selectedAccount = ref<AccountSearchResultV1 | null>(null)

const targetAccountUsers = ref<AccountUserV1[] | undefined>()
const loadingTargetUsers = ref(false)
const loadedTargetUsers = ref(false)
const targetAccountExists = ref(false)
const movingSelf = ref(false)

const canMove = computed(() => {
  if (!selectedAccount.value) return false
  if (selectedAccount.value.accountId === myAccountId.value) return false
  if (!loadedTargetUsers.value) return false
  if (!targetAccountExists.value) return false
  return true
})

async function onSearchAccounts(event: { query: string }): Promise<void> {
  const query = event.query.trim()
  if (!query || query.length < SEARCH_MIN_LENGTH) {
    accountSuggestions.value = []
    return
  }
  try {
    loadingAccountSearch.value = true
    const result = await useApi().searchAccounts({ searchAccountsV1: { searchTerm: query } })
    accountSuggestions.value = result.accounts.map((a) => ({
      ...a,
      label: `#${a.accountId} – ${[a.firstName, a.lastName].filter(Boolean).join(' ')} (${a.email})`
    }))
    log('account search returned', result.accounts.length, 'results')
  } catch (err) {
    logError('account search failed:', err)
  } finally {
    loadingAccountSearch.value = false
  }
}

function onAccountSelected(event: { value: AccountSuggestion }): void {
  selectedAccount.value = event.value
  loadTargetAccountUsers(event.value.accountId)
}

function onSearchCleared(): void {
  selectedAccount.value = null
  targetAccountUsers.value = undefined
  loadedTargetUsers.value = false
  targetAccountExists.value = false
}

async function loadTargetAccountUsers(accountId: number): Promise<void> {
  try {
    loadedTargetUsers.value = false
    loadingTargetUsers.value = true
    targetAccountUsers.value = undefined
    const { users } = await useApi().getAccountUsersV1({ accountId })
    loadedTargetUsers.value = true
    targetAccountUsers.value = users
    targetAccountExists.value = true
    log('loaded', users.length, 'users for account', accountId)
  } catch (error) {
    if (isApiNotFoundError(error)) {
      log('account', accountId, 'not found')
      loadedTargetUsers.value = true
      targetAccountExists.value = false
    } else {
      logError('failed to load account users:', error)
      handleApiError(() => { throw error })
    }
  } finally {
    loadingTargetUsers.value = false
  }
}

async function moveSelf(): Promise<void> {
  const targetAccountId = selectedAccount.value?.accountId
  const userId = mySelf.value?.userId
  if (!targetAccountId || !userId) {
    const errorMsg = !userId ? 'User profile not loaded' : 'No target account selected'
    logWarn('move account cancelled:', errorMsg)
    toast.add({ severity: 'error', summary: 'Not ready', detail: 'Your user profile is not loaded yet. Try again.', life: TOAST_DURATION })
    return
  }
  handleApiError(
    async () => {
      try {
        movingSelf.value = true
        log('moving user', userId, 'to account', targetAccountId)
        await useApi().putAccountUser({ accountId: targetAccountId, putAccountUserRequestV1: { userId } })
        myAccountId.value = targetAccountId
        onSearchCleared()
        accountSearch.value = null
        log('successfully moved to account', targetAccountId)
        toast.add({ severity: 'success', summary: 'Account moved', detail: `Moved to account #${targetAccountId} — app refreshed!`, life: TOAST_DURATION })
        refreshIframe()
      } finally {
        movingSelf.value = false
      }
    }
  )
}

/**
 * Silently obtain the portal's access token and forward it to the
 * mobile app iframe via postMessage so it can skip its own Auth0 redirect
 * (which would be blocked inside an iframe anyway).
 */
async function sendTokenToIframe(): Promise<void> {
  if (!iframeRef.value?.contentWindow) {
    logWarn('iframe contentWindow not available')
    return
  }
  try {
    log('requesting access token silently')
    const access_token = await getAccessTokenSilentlyOutsideComponent()
    const id_token = auth0.idTokenClaims.value?.__raw
    const message: IframeMessage = { type: 'AUTH_TOKEN', access_token, id_token }
    iframeRef.value.contentWindow.postMessage(message, APP_ORIGIN)
    log('auth token posted to iframe')
  } catch (err) {
    logError('failed to obtain access token:', err)
  }
}

/** Reload the iframe (re-sends the auth token after load). */
function refreshIframe(): void {
  if (iframeRef.value?.src) {
    log('refreshing iframe')
    iframeRef.value.src = iframeRef.value.src
  }
}

/** Called once the iframe document has finished loading. */
function onIframeLoad(): void {
  log('iframe loaded')
  sendTokenToIframe()
}

/**
 * Validates and handles messages from the iframe.
 * The iframe can post { type: 'REQUEST_TOKEN' } when it needs a fresh token.
 */
function handleMessage(event: MessageEvent<Partial<IframeMessage>>): void {
  // Security: Always validate origin first
  if (!isValidMessageOrigin(event.origin)) {
    logWarn('rejected message from untrusted origin:', event.origin)
    return
  }

  const { type } = event.data ?? {}
  if (type === 'REQUEST_TOKEN') {
    log('iframe requesting new auth token')
    sendTokenToIframe()
  }
}

onMounted(() => {
  breadCrumbsStore.$patch({ items: [{ text: 'Gecko App' }] })
  window.addEventListener('message', handleMessage)
  log('component mounted')
})

onUnmounted(() => {
  window.removeEventListener('message', handleMessage)
  log('component unmounted')
})
</script>

<style scoped>
/* ── Page layout ── */
.amplify-container {
  height: calc(100vh - 64px);
  padding: 16px 40px !important;
  overflow: hidden;
  display: flex;
  align-items: stretch;
}

.layout-wrapper {
  display: flex;
  height: 100%;
  flex: 1;
  gap: 40px;
  align-items: flex-start;
}

/* ── Phone section ── */
.phone-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.phone-outer-frame {
  position: relative;
  height: min(100%, 980px);
  aspect-ratio: 450 / 844;
  /* iPhone-style dark aluminium gradient */
  background: linear-gradient(145deg, #3a3a3c 0%, #1c1c1e 55%, #2c2c2e 100%);
  border-radius: 13.5%;
  padding: 12px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.12),
    inset 0 0 0 2.5px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(0, 0, 0, 0.95),
    0 30px 80px rgba(0, 0, 0, 0.6),
    0 8px 24px rgba(0, 0, 0, 0.4);
}

/* ── Hardware side buttons ── */
.phone-btn {
  position: absolute;
}

/* Left-side: silent switch + volume up/down */
.btn-silent,
.btn-vol-up,
.btn-vol-down {
  left: -3.5px;
  width: 3.5px;
  background: linear-gradient(to right, #1a1a1c, #3a3a3c);
  border-radius: 2px 0 0 2px;
}

.btn-silent   { top: 12%; height: 4%;  }
.btn-vol-up   { top: 20%; height: 7%;  }
.btn-vol-down { top: 29%; height: 7%;  }

/* Right-side: power / lock button */
.btn-power {
  right: -3.5px;
  width: 3.5px;
  height: 10%;
  top: 21%;
  background: linear-gradient(to left, #1a1a1c, #3a3a3c);
  border-radius: 0 2px 2px 0;
}

/* ── Screen area ── */
.phone-screen {
  width: 100%;
  height: 100%;
  background: #000;
  border-radius: 9%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

/* App iframe */
.amplify-iframe {
  border: none;
  display: block;
  width: 100%;
  flex: 1;
  min-height: 0;
}

/* Home indicator bar */
.home-indicator {
  position: absolute;
  bottom: 1.5%;
  left: 50%;
  transform: translateX(-50%);
  width: 28%;
  height: 4px;
  background: rgba(255, 255, 255, 0.38);
  border-radius: 10px;
  z-index: 10;
}

/* ── Tools panel (right) ── */
.tools-section {
  width: 320px;
  flex-shrink: 0;
  height: 100%;
  overflow-y: auto;
}

.tools-panel {
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tools-panel-title {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #aaa;
  padding: 14px 20px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.tools-panel-body {
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
}

.tools-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 0.875rem;
  color: inherit;
  transition: background 0.15s;
}

.tools-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.tools-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #bbb;
  padding: 14px 0 8px;
}

.tools-inner {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.current-account-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #555;
  padding: 10px 14px;
  background: #f5f5f5;
  border-radius: 8px;
}

.selected-account-preview {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.selected-account-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.target-users-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}

.target-user-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: #444;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}


</style>
